import React, { useState } from 'react';
import { Search, User, Trophy, ArrowLeft } from 'lucide-react';
import { useCustomers } from '../../hooks/useCustomers';
import { usePoints } from '../../hooks/usePoints';
import { useSlotMachine } from '../../hooks/useSlotMachine';
import { validators } from '../../utils/validators';
import { formatters } from '../../utils/formatters';
import LoadingSpinner from '../common/LoadingSpinner';
import SlotMachineAnimation from '../common/SlotMachineAnimation';

const AddPointsPage = () => {
  const [searchDocument, setSearchDocument] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [formData, setFormData] = useState({
    points: '',
    description: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);

  const { customers, updateCustomerPoints } = useCustomers();
  const { addPoints } = usePoints();
  const { animationData, showPointsAnimation, hideAnimation, resetAnimation } = useSlotMachine();

  const handleSearchCustomer = async () => {
    if (!validators.isValidCpfOrCnpj(searchDocument)) {
      const docType = validators.getDocumentType(searchDocument);
      if (docType === 'unknown') {
        setErrors({ search: 'CPF ou CNPJ inválido' });
      } else {
        setErrors({ search: `${docType === 'cpf' ? 'CPF' : 'CNPJ'} inválido` });
      }
      return;
    }

    setSearching(true);
    setErrors({});
    
    try {
      // Buscar cliente pelo documento na lista local
      const customer = customers.find(c => 
        formatters.cleanCpfOrCnpj(c.cpf) === formatters.cleanCpfOrCnpj(searchDocument)
      );

      if (customer) {
        setSelectedCustomer(customer);
      } else {
        setErrors({ search: 'Cliente não encontrado' });
      }
    } catch (error) {
      setErrors({ search: 'Erro ao buscar cliente' });
    } finally {
      setSearching(false);
    }
  };

  const validatePointsForm = () => {
    const newErrors = {};

    if (!validators.isValidPoints(formData.points)) {
      newErrors.points = 'Digite uma quantidade válida de pontos';
    }

    if (formData.description.length > 200) {
      newErrors.description = 'Descrição deve ter no máximo 200 caracteres';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddPoints = async (e) => {
    e.preventDefault();
    
    if (!validatePointsForm()) return;

    setLoading(true);
    try {
      const oldPoints = selectedCustomer.points || 0;
      const pointsToAdd = parseInt(formData.points);
      const newPoints = oldPoints + pointsToAdd;
      
      // Reset qualquer animação anterior
      resetAnimation();
      
      await addPoints(selectedCustomer.id, pointsToAdd, formData.description);
      
      // Atualizar pontos do cliente localmente
      updateCustomerPoints(selectedCustomer.id, pointsToAdd);
      
      // Aguardar um pouco antes de mostrar a animação
      setTimeout(() => {
        showPointsAnimation(oldPoints, newPoints, 3500);
      }, 100);
      
      // Aguardar animação terminar
      setTimeout(() => {
        // Limpar formulário após sucesso
        setFormData({ points: '', description: '' });
        setSelectedCustomer(null);
        setSearchDocument('');
      }, 4000);
      
    } catch (error) {
      setErrors({ submit: error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setSelectedCustomer(null);
    setFormData({ points: '', description: '' });
    setErrors({});
    resetAnimation(); // Reset da animação ao voltar
  };

  const handleDocumentChange = (e) => {
    const formatted = formatters.formatCpfOrCnpj(e.target.value);
    setSearchDocument(formatted);
    setErrors({});
  };

  return (
    <div className="max-w-2xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center">
          <Trophy className="mr-3 h-8 w-8 text-blue-600" />
          Adicionar Pontos
        </h1>
        <p className="mt-2 text-gray-600">
          Encontre um cliente por CPF ou CNPJ e adicione pontos à sua conta
        </p>
      </div>

      {!selectedCustomer ? (
        // Etapa 1: Buscar Cliente
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Buscar Cliente
          </h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                CPF/CNPJ do Cliente
              </label>
              <div className="flex space-x-3">
                <input
                  type="text"
                  value={searchDocument}
                  onChange={handleDocumentChange}
                  className={`flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.search ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="000.000.000-00 ou 00.000.000/0000-00"
                  maxLength="18"
                />
                <button
                  onClick={handleSearchCustomer}
                  disabled={searching || !searchDocument}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {searching ? (
                    <LoadingSpinner size="sm" />
                  ) : (
                    <Search className="w-4 h-4" />
                  )}
                  <span>Buscar</span>
                </button>
              </div>
              {errors.search && <p className="text-sm text-red-600 mt-1">{errors.search}</p>}
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex">
                <User className="w-5 h-5 text-blue-600 mr-2 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium">Dica:</p>
                  <p>Digite o CPF (11 dígitos) ou CNPJ (14 dígitos) do cliente para buscar em sua base de dados.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        // Etapa 2: Adicionar Pontos
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Adicionar Pontos
            </h2>
            <button
              onClick={handleBack}
              className="text-blue-600 hover:text-blue-700 flex items-center space-x-1"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Voltar</span>
            </button>
          </div>

          {/* Informações do Cliente */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Cliente Selecionado</h3>
            <div className="space-y-1">
              <p className="font-medium text-gray-900">{selectedCustomer.name}</p>
              <p className="text-sm text-gray-600">
                Documento: {formatters.formatCpfOrCnpj(selectedCustomer.cpf)}
                {selectedCustomer.cpf && (
                  <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                    {selectedCustomer.cpf.length === 11 ? 'CPF' : selectedCustomer.cpf.length === 14 ? 'CNPJ' : 'Doc'}
                  </span>
                )}
              </p>
              <p className="text-sm text-gray-600">Pontos atuais: {selectedCustomer.points || 0}</p>
            </div>
          </div>

          {/* Formulário de Pontos */}
          <form onSubmit={handleAddPoints} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Quantidade de Pontos *
              </label>
              <input
                type="number"
                value={formData.points}
                onChange={(e) => setFormData({ ...formData, points: e.target.value })}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.points ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Digite a quantidade de pontos"
                min="1"
                max="10000"
              />
              {errors.points && <p className="text-sm text-red-600 mt-1">{errors.points}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Descrição *
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.description ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Motivo da adição de pontos (ex: Compra de R$ 100,00)"
                rows="3"
                maxLength="200"
              />
              {errors.description && <p className="text-sm text-red-600 mt-1">{errors.description}</p>}
              <p className="text-sm text-gray-500 mt-1">
                {formData.description.length}/200 caracteres
              </p>
            </div>

            {errors.submit && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{errors.submit}</p>
              </div>
            )}

            <div className="flex space-x-3 pt-4">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  <Trophy className="w-4 h-4" />
                )}
                <span>{loading ? 'Adicionando...' : 'Adicionar Pontos'}</span>
              </button>
              <button
                type="button"
                onClick={handleBack}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Animação de Caça-níquel */}
      <SlotMachineAnimation
        isVisible={animationData.isVisible}
        onClose={hideAnimation}
        type={animationData.type}
        oldValue={animationData.oldValue}
        newValue={animationData.newValue}
        rewardName={animationData.rewardName}
        duration={animationData.duration}
      />
    </div>
  );
};

export default AddPointsPage;
